import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Users, ListChecks, ShieldCheck, Calculator } from "lucide-react";

const SECTIONS = [
  {
    id: "salary-flow",
    title: "Нарахування зарплати — за чим заповнювати",
    icon: Calculator,
    intro: "Зарплата нараховується автоматично після того, як операція переходить у статус «Проведено». Щоб логіка спрацювала правильно — дотримуйтеся послідовності.",
    blocks: [
      {
        h: "1. Створіть пацієнта",
        steps: [
          "Меню «Пацієнти» → «+ Додати».",
          "Заповніть ПІБ, телефон (унікальний — система блокує дублікати), адресу.",
          "Поле «Звідки дізнався» — ТІЛЬКИ для маркетингової аналітики. На зарплату НЕ впливає.",
        ],
      },
      {
        h: "2. Створіть рекомендацію",
        steps: [
          "Меню «Рекомендації» → «+ Створити», або в картці пацієнта → «Створити рекомендацію».",
          "Оберіть процедуру (ФЕК OD/OS, LASIK OD/OS тощо) та орієнтовну суму.",
          "ВАЖЛИВО: оберіть «Тип пацієнта»:",
          "  • Первинний → пацієнт прийшов сам.",
          "  • Направлений → пацієнт прийшов за направленням (від лікаря, з відрядження, від партнера тощо).",
          "Якщо обрали «Направлений» — оберіть «Джерело направлення» з довідника (Налаштування → Джерела направлення).",
          "Якщо джерело має «Потребує бригаду» = Так — заповніть блок «Бригада направлення» (мінімум одна особа).",
          "Якщо джерело = «Зовнішній лікар» — оберіть лікаря з довідника «Зовнішні лікарі».",
          "Заповніть блок «Персонал клініки» — це особи з вашої клініки, які працювали з цим пацієнтом. Саме вони отримають нарахування за роботу.",
        ],
      },
      {
        h: "3. Створіть операційний день",
        steps: [
          "Меню «Операційні дні» → «+ Додати день».",
          "Оберіть дату та призначте хірургів. Хірурги отримують свою частку окремо.",
        ],
      },
      {
        h: "4. Додайте операцію в операційний день",
        steps: [
          "Відкрийте операційний день → «+ Додати операцію».",
          "Виберіть пацієнта → потім обов'язково виберіть рекомендацію + конкретний рядок (recommendation_item).",
          "ВАЖЛИВО: ІОЛ та діоптрія вводяться вручну — це фактичні дані операції.",
          "При виборі моделі ІОЛ — сума автоматично підставляється з прайс-листа.",
          "Збережіть зі статусом «Заплановано». Зарплата ще НЕ нараховується.",
        ],
      },
      {
        h: "5. Проведіть операцію (статус «Проведено»)",
        steps: [
          "У картці операції змініть статус на «Проведено».",
          "Тільки тут запускається нарахування зарплати! Salary engine:",
          "  1. Бере recommendation_item_id з операції.",
          "  2. Зчитує з рекомендації: Тип пацієнта + Персонал клініки + (опц.) Бригаду направлення.",
          "  3. Застосовує правила з Налаштування → Формули ЗП.",
        ],
      },
    ],
  },
  {
    id: "employee-earnings",
    title: "За що ви заробляєте — для працівника",
    icon: Calculator,
    intro: "Розділ для персоналу: як читати свою зарплатну відомість і зрозуміти, звідки взялась кожна гривня.",
    blocks: [
      {
        h: "Головний принцип",
        steps: [
          "Ви отримуєте свій ПОВНИЙ відсоток від суми операції, якщо ваше ім'я вказано в рекомендації пацієнта і операція проведена.",
          "Відсотки НЕ діляться між учасниками. Якщо в команді 3 фельдшери — кожен отримує свої повні 1.5%, а не по 0.5%.",
          "Нарахування з'являється тільки коли статус операції змінено на «Проведено» (або «Прооперований»).",
        ],
        accent: "sky",
      },
      {
        h: "Скільки я отримую за різні типи пацієнтів",
        steps: [
          "Ваша ставка залежить від типу пацієнта в рекомендації, до якої ви прив'язані.",
          "Первинний пацієнт (новий, прийшов сам): найвищі ставки.",
          "Направлений (від лікаря/партнера): нижчі ставки, але стабільніший потік.",
          "Після 3 міс (пацієнт повернувся після того, як попередня рекомендація прострочилась): ставки на рівні направленого.",
          "Відрядження (виїзні бригади — Залізнична 7, Виїзд тощо): додатковий бонус.",
        ],
        accent: "violet",
      },
      {
        h: "Приклад: операція на 30 000 ₴, первинний пацієнт",
        steps: [
          "Лікар (3%): 900 ₴",
          "Менеджер (2.5%): 750 ₴",
          "Фельдшер (1.5%): 450 ₴ — кожен фельдшер у команді",
          "Фахівець оптики (0.5%): 150 ₴",
          "Реєстратор (0.5%): 150 ₴",
          "ЯКЩО операція з виїзду — додатково бригада: лікар +1.5% (450 ₴), менеджер +1% (300 ₴), оптометрист +1% (300 ₴).",
        ],
        accent: "rose",
      },
      {
        h: "Що зробити, щоб отримати свою долю",
        steps: [
          "1. Переконайтеся, що ваш менеджер вписав ВАС у блок «Персонал клініки» у рекомендації пацієнта (для роботи в клініці) або «Бригада направлення» (для виїздів).",
          "2. Перевірте свою роль — система платить по ролі, тому якщо ви фельдшер, але вас вписали як реєстратора, ви отримаєте 0.5% замість 1.5%.",
          "3. Якщо ваше прізвище немає в рекомендації — ви НЕ отримаєте нарахування за цю операцію.",
          "4. Зарплата з'являється АВТОМАТИЧНО після того, як операція переведена в статус «Проведено». Не треба нічого додатково підтверджувати.",
        ],
        accent: "teal",
      },
      {
        h: "Коли я НЕ отримаю нарахування",
        steps: [
          "Операція в статусі «Заплановано», «Скасовано» або «Не з'явився» — нарахування немає.",
          "Рекомендація закрита (значок 🔒) — нарахування за майбутніми операціями зупинене.",
          "Ваше прізвище не вписане в блок «Персонал клініки»/«Бригада» — пройде повз вас.",
          "Якщо хтось видалив операцію — ваше нарахування за неї автоматично знімається.",
        ],
      },
      {
        h: "Як перевірити свою зарплату",
        steps: [
          "Меню «Виплати» → оберіть себе → побачите всі нараховані суми по операціях.",
          "Колонка «Нараховано» — те що ви заробили. «Виплачено» — те, що вже отримали готівкою/на картку. «Борг» — те, що клініка має сплатити.",
          "Якщо ви бачите помилку (наприклад, вас немає в команді, хоча ви точно працювали) — звертайтеся до Директора або Адміністратора. Тільки вони можуть скоригувати.",
        ],
        accent: "sky",
      },
    ],
  },
  {
    id: "salary-rules",
    title: "Формули зарплати — офіційні ставки",
    icon: ShieldCheck,
    intro: "Налаштування → Формули ЗП. ВАЖЛИВО: кожен працівник, вказаний у рекомендації, отримує СВІЙ ПОВНИЙ %. Відсотки НЕ діляться між учасниками. Наприклад, якщо в рекомендації 3 фельдшери — кожен отримає 1.5% (а НЕ 0.5%).",
    blocks: [
      {
        h: "Категорія 1: Первинний пацієнт",
        steps: [
          "Тип пацієнта = Первинний у рекомендації.",
          "Лікар — 3.0%",
          "Менеджер — 2.5%",
          "Фельдшер — 1.5%",
          "Реєстратор — 0.5%",
          "Фахівець оптики — 0.5%",
        ],
        accent: "sky",
      },
      {
        h: "Категорія 2: Направлений пацієнт",
        steps: [
          "Тип пацієнта = Направлений у рекомендації.",
          "Лікар — 2.0%",
          "Менеджер — 1.5%",
          "Фельдшер — 1.0%",
          "Реєстратор — 0.5%",
          "Фахівець оптики — 0.5%",
        ],
        accent: "violet",
      },
      {
        h: "Категорія 3: Бригада відрядження (додатковий бонус)",
        steps: [
          "Активується тільки для джерел з 'Потребує бригаду = Так' (Відрядження, Залізнична 7, тощо).",
          "Лікар відрядження — 1.5%",
          "Менеджер відрядження — 1.0%",
          "Оптометрист відрядження — 1.0%",
          "Фахівець оптики відрядження — 1.0%",
        ],
        accent: "teal",
      },
      {
        h: "Категорія 4: Після 3 місяців",
        steps: [
          "Якщо первинний пацієнт НЕ прооперувався протягом 90 днів від дати створення рекомендації, попередня рекомендація вважається простроченою.",
          "Створюється НОВА рекомендація. Зарплата нараховується по колонці «Після 3 міс».",
          "Тип пацієнта залишається «Первинний», але ставки інші.",
        ],
        accent: "rose",
      },
    ],
  },
  {
    id: "personnel",
    title: "Персонал — як розподілити по групах",
    icon: Users,
    intro: "Меню «Працівники» має три вкладки. Один працівник може належати до однієї або обох груп (клініка + відрядження).",
    blocks: [
      {
        h: "Персонал клініки",
        steps: [
          "Працівники, які виконують функції в клініці (лікар, менеджер, реєстратор, фахівець оптики, фельдшер).",
          "У формі рекомендації з'являються в блоці «Персонал клініки».",
          "Отримують нарахування за категоріями «Первинний» або «Направлений».",
        ],
      },
      {
        h: "Персонал відрядження",
        steps: [
          "Працівники, які їздять у виїзди (Залізнична 7, інші клініки).",
          "У формі рекомендації з'являються в блоці «Бригада направлення» — тільки якщо обране джерело має «Потребує бригаду» = Так.",
          "Отримують нарахування за категорією «Бригада відрядж.».",
        ],
      },
      {
        h: "Зовнішні лікарі",
        steps: [
          "Лікарі з інших клінік, які направляють пацієнтів.",
          "НЕ є працівниками Global Vision і НЕ отримують зарплату через систему.",
          "Використовуються тільки як «джерело» направлення у рекомендації.",
        ],
      },
    ],
  },
  {
    id: "important",
    title: "Важливо знати",
    icon: ListChecks,
    intro: "Особливості, які впливають на правильність нарахування.",
    blocks: [
      {
        h: "Закриття рекомендації",
        steps: [
          "Якщо рекомендацію ЗАКРИТО (кнопка «замок») — за нею НЕ нараховується зарплата на майбутні операції.",
          "Це використовується, коли пацієнт відмовився або потрібно явно зняти блокування дублювань.",
        ],
      },
      {
        h: "Видалення рекомендації",
        steps: [
          "Тільки Директор та Адміністратор можуть видаляти рекомендації (кнопка «корзина»).",
          "Якщо до рекомендації прив'язані ПРОВЕДЕНІ операції — видалення блокується. Спочатку видаліть або скасуйте операції.",
        ],
      },
      {
        h: "Дублювання операцій",
        steps: [
          "Не можна створити дві операції на той самий recommendation_item, поки попередня не Скасована або Видалена.",
          "Це захищає від подвійного нарахування ЗП.",
        ],
      },
      {
        h: "Перерахунок ЗП",
        steps: [
          "При редагуванні операції (зміна суми, ІОЛ, статусу) — ЗП автоматично перераховується.",
          "При зміні правил у Налаштування → Формули ЗП — старі нарахування НЕ перераховуються, тільки нові операції.",
        ],
      },
    ],
  },
];

export default function Instructions() {
  return (
    <div className="space-y-6" data-testid="instructions-page">
      <div className="flex items-center gap-3">
        <BookOpen className="h-8 w-8 text-[#0D9488]" />
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Інструкції</h1>
          <p className="text-sm text-slate-500">Як заповнювати документи в правильному порядку, щоб ЗП нараховувалася коректно.</p>
        </div>
      </div>

      <Card className="gv-card">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Зміст</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {SECTIONS.map((s) => {
              const Ic = s.icon;
              return (
                <a key={s.id} href={`#${s.id}`} className="flex items-center gap-2 text-sm text-[#0F2027] hover:text-[#0D9488] hover:underline">
                  <Ic className="h-4 w-4 text-[#0D9488]" />
                  {s.title}
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {SECTIONS.map((section) => {
        const Ic = section.icon;
        return (
          <Card key={section.id} id={section.id} className="gv-card scroll-mt-24" data-testid={`section-${section.id}`}>
            <CardContent className="p-5 md:p-7">
              <div className="flex items-center gap-2 mb-3">
                <Ic className="h-6 w-6 text-[#0D9488]" />
                <h2 className="text-xl font-bold text-[#0F2027]">{section.title}</h2>
              </div>
              <p className="text-sm text-slate-600 mb-5">{section.intro}</p>
              <div className="space-y-5">
                {section.blocks.map((b, i) => {
                  const accentClass = {
                    sky: "border-sky-200 bg-sky-50/40",
                    violet: "border-violet-200 bg-violet-50/40",
                    teal: "border-teal-200 bg-teal-50/40",
                  }[b.accent] || "border-slate-200 bg-slate-50/40";
                  return (
                    <div key={i} className={`border-l-4 ${accentClass} p-4 rounded-r-md`}>
                      <div className="font-semibold text-[#0F2027] mb-2">{b.h}</div>
                      <ul className="space-y-1.5 text-sm text-slate-700">
                        {b.steps.map((step, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="text-[#0D9488] font-bold">•</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Card className="gv-card bg-slate-50">
        <CardContent className="p-5 text-center">
          <Badge className="bg-teal-50 text-teal-700 mb-2">Динамічна сторінка</Badge>
          <p className="text-xs text-slate-500">
            Інструкція оновлюється при додаванні нових модулів. Якщо щось незрозуміло — зверніться до Адміністратора або Директора.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
